/*
 * Copyright (C) 2018 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import {Button} from '@instructure/ui-buttons'
import {Heading} from '@instructure/ui-heading'
import {Spinner} from '@instructure/ui-spinner'
import {Tabs} from '@instructure/ui-tabs'
import {View} from '@instructure/ui-view'
import {Text} from '@instructure/ui-text'
import {Alert} from '@instructure/ui-alerts'

import {useScope as createI18nScope} from '@canvas/i18n'
import React from 'react'
import PropTypes from 'prop-types'
import AdminTable from './AdminTable'
import InheritedTable from './InheritedTable'
import DeveloperKey from './DeveloperKey'
import NewKeyModal from './NewKeyModal'
import {showFlashAlert, showFlashSuccess} from '@instructure/platform-alerts'
import DateHelper from '@canvas/datetime/dateHelper'
import {DynamicRegistrationModal} from './dynamic_registration/DynamicRegistrationModal'
import {Flex} from '@instructure/ui-flex'

const I18n = createI18nScope('react_developer_keys')
/**
 * @see {@link DeveloperKeysApp.developerKeySaveSuccessfulHandler}
 * @description
 * How long to wait after closing NewKeyModal to show any developer key alerts.
 * This value might be able to be decreased, but two seconds is a pretty
 * safe gap.
 */
const ALERT_WAIT_TIME = 2000

class DeveloperKeysApp extends React.Component {
  state = {
    focusTab: false,
    selectedTab: 'tab-panel-account',
  }

  get isSiteAdmin() {
    return this.props.ctx.params.contextId === 'site_admin'
  }

  setMainTableRef = node => {
    this.mainTableRef = node
  }

  setInheritedTableRef = node => {
    this.inheritedTableRef = node
  }

  setInheritedTabRef = node => {
    this.inheritedTab = node
  }

  focusInheritedTab = () => {
    this.setState({focusTab: true})
  }

  showMoreButtonHandler = _event => {
    const {
      applicationState: {
        listDeveloperKeys: {nextPage},
      },
      store: {dispatch},
      actions: {getRemainingDeveloperKeys},
    } = this.props
    const callBack = this.mainTableRef.setFocusCallback()
    getRemainingDeveloperKeys(nextPage, [], callBack)(dispatch)
  }

  showMoreButton() {
    const {
      applicationState: {
        listDeveloperKeys: {listDeveloperKeysPending, nextPage},
      },
    } = this.props

    if (nextPage && !listDeveloperKeysPending) {
      return (
        <Button data-testid="show-all-account" onClick={this.showMoreButtonHandler}>
          {I18n.t('Show All Keys')}
        </Button>
      )
    }
    return null
  }

  showMoreInheritedButtonHandler = _event => {
    const {
      applicationState: {
        listDeveloperKeys: {inheritedNextPage},
      },
      store: {dispatch},
      actions: {getRemainingInheritedDeveloperKeys},
    } = this.props

    const callBack = this.inheritedTableRef.setFocusCallback()
    getRemainingInheritedDeveloperKeys(inheritedNextPage, [], callBack)(dispatch)
  }

  showMoreInheritedButton() {
    const {
      applicationState: {
        listDeveloperKeys: {listInheritedDeveloperKeysPending, inheritedNextPage},
      },
    } = this.props

    if (inheritedNextPage && !listInheritedDeveloperKeysPending) {
      return (
        <Button data-testid="show-all-inherited" onClick={this.showMoreInheritedButtonHandler}>
          {I18n.t('Show All Keys')}
        </Button>
      )
    }
    return null
  }

  changeTab(_ev, {id}) {
    this.setState({selectedTab: id})
  }

  /**
   * Due to some annoying accessibility issues related to modal focus
   * returning and screenreader issues, we have to use a setTimeout here
   * to make sure either alert gets read out properly. Without a setTimeout,
   * the alert shows up, but because the Modal returns focus back to the element
   * that opened it, the screenreader starts and then immediately stops reading
   * the alert, instead reading out the description of the edit developer key
   * button. If you can find a better solution, please remove this hacky
   * workaround and do it.
   * @todo Find a better way to avoid modal-focus-screenreader-bulldozing so
   * this isn't necessary.
   * @param {string | string[]} warningMessage - A warning message or a list of warning messages to show to the user.
   */
  developerKeySaveSuccessfulHandler(warningMessage) {
    setTimeout(() => {
      showFlashSuccess(I18n.t('Save successful.'))()
      if (Array.isArray(warningMessage)) {
        for (const message of warningMessage) {
          if (message) {
            showFlashAlert({message, type: 'warning'})
          }
        }
      } else if (warningMessage) {
        showFlashAlert({message: warningMessage, type: 'warning'})
      }
    }, ALERT_WAIT_TIME)
  }

  alerts() {
    return [
      {
        text: [
          I18n.t(
            `API GET requests with a body instead of query parameters will be blocked in Production soon, as part of the transition to serving Canvas using AWS CloudFront. These requests are already blocked in Beta and Test.`,
          ),
          I18n.t(
            `You are being notified because this Canvas instance has recently received requests which would be blocked. You can review any current GET requests with a body on the *Reports Page*, read more in the **API Changelog**, and learn how to mitigate this change in the blog post linked there.`,
            {
              wrappers: [
                `<a data-pendo='dev-key-reports-page' target='_blank' href='/accounts/${window.ENV.ACCOUNT_ID}/settings#tab-reports' style='text-decoration: underline'>$1</a>`,
                `<a data-pendo='api-cdn-change-log' target='_blank' href='https://community.instructure.com/en/discussion/664378/2026-api-and-cli-change-log' style='text-decoration: underline'>$1</a>`,
              ],
            },
          ),
        ],
        type: 'warning',
        shouldShow: ENV.showApiGetWithBodyNotice,
      },
      {
        text: [
          I18n.t(
            `API requests now require the User-Agent header to be set. These requests are now blocked in Beta, and enforcement in Test and Production is coming soon - please see the *API Change Log* for dates. `,
            {
              wrappers: [
                `<a data-pendo='dev-key-change-log' target='_blank' href='https://community.instructure.com/en/discussion/626858/2025-api-and-cli-change-log/p1' style='text-decoration: underline'>$1</a>`,
              ],
            },
          ),
          I18n.t(
            `You can review any current calls without a User-Agent set in Test and Production on the *Reports Page* until the enforcement date, and read more about the change on our **blog**.`,
            {
              wrappers: [
                `<a data-pendo='dev-key-reports-page' target='_blank' href='/accounts/${window.ENV.ACCOUNT_ID}/settings#tab-reports' style='text-decoration: underline'>$1</a>`,
                `<a data-pendo='dev-key-blog' target='_blank' href='https://community.canvaslms.com/t5/Canvas-LMS-Blog/Enforcing-User-Agent-Header-for-Canvas-API-Requests/ba-p/658205' style='text-decoration: underline'>$1</a>`,
              ],
            },
          ),
        ],
        type: 'warning',
        shouldShow: ENV.FEATURES?.developer_key_user_agent_alert,
      },
      {
        text: [
          I18n.t(
            `LTI tool management is now live in *Canvas Apps*! Changes sync between both pages as we develop more features for Apps. From now on, Apps is the primary home for LTI tools.`,
            {
              wrappers: [
                `<a data-pendo='dev-key-apps-link' target='_blank' href='/accounts/${window.ENV.ACCOUNT_ID}/apps/manage' style='text-decoration: underline'>$1</a>`,
              ],
            },
          ),
        ],
        type: 'info',
        shouldShow: true,
      },
    ]
  }

  render() {
    const {
      applicationState: {
        listDeveloperKeys: {
          list,
          inheritedList,
          listDeveloperKeysPending,
          listInheritedDeveloperKeysPending,
        },
        createOrEditDeveloperKey,
        listDeveloperKeyScopes,
      },
      store,
      actions,
      ctx,
    } = this.props
    const tab = this.state.selectedTab
    const globalInheritedList = (inheritedList || []).filter(key => key.inherited_from === 'global')
    const parentInheritedList = (inheritedList || []).filter(
      key => key.inherited_from === 'federated_parent',
    )

    return (
      <div>
        <View as="div" margin="none" padding="none">
          <Heading level="h1">{I18n.t('Developer Keys')}</Heading>
        </View>
        {this.alerts()
          .filter(alert => alert.shouldShow)
          .map((alert, i) => (
            <Alert
              key={`alert-${i}`}
              margin="medium 0"
              variant={alert.type || 'info'}
              hasShadow={false}
            >
              <Flex gap="xx-small" direction="column">
                {alert.text.map((text, j) => (
                  <Text
                    key={j}
                    dangerouslySetInnerHTML={{
                      __html: text,
                    }}
                  />
                ))}
              </Flex>
            </Alert>
          ))}
        <Tabs
          onRequestTabChange={this.changeTab.bind(this)}
          shouldFocusOnRender={this.state.focusTab}
          data-testid="developer-keys-tabs"
        >
          <Tabs.Panel
            renderTitle={I18n.t('Account')}
            id="tab-panel-account"
            isSelected={tab === 'tab-panel-account'}
            data-testid="account-tab"
          >
            <NewKeyModal
              store={store}
              actions={actions}
              createOrEditDeveloperKeyState={createOrEditDeveloperKey}
              availableScopes={listDeveloperKeyScopes.availableScopes}
              availableScopesPending={listDeveloperKeyScopes.listDeveloperKeyScopesPending}
              selectedScopes={listDeveloperKeyScopes.selectedScopes}
              ctx={ctx}
              handleSuccessfulSave={this.developerKeySaveSuccessfulHandler}
            />
            <DynamicRegistrationModal contextId={this.props.ctx.params.contextId} store={store} />
            <AdminTable
              ref={this.setMainTableRef}
              store={store}
              actions={actions}
              developerKeysList={list}
              ctx={ctx}
              setFocus={this.focusDevKeyButton}
              data-testid="dev-key-admin-table"
            />
            <View as="div" margin="small" padding="large" textAlign="center">
              {listDeveloperKeysPending ? <Spinner renderTitle={I18n.t('Loading')} /> : null}
              {this.showMoreButton()}
            </View>
          </Tabs.Panel>
          {this.isSiteAdmin ? null : (
            <Tabs.Panel
              renderTitle={I18n.t('Inherited')}
              elementRef={this.setInheritedTabRef}
              id="tab-panel-inherited"
              isSelected={tab === 'tab-panel-inherited'}
              data-testid="inherited-tab"
            >
              <div data-testid="inherited-tab-content">
                {parentInheritedList.length > 0 && (
                  <>
                    <Heading margin="small" level="h2">
                      {I18n.t('Consortium Parent Keys')}
                    </Heading>
                    <InheritedTable
                      prefix="parent"
                      label={I18n.t('Parent Inherited Developer Keys')}
                      store={store}
                      actions={actions}
                      developerKeysList={parentInheritedList}
                      ctx={ctx}
                      data-testid="dev-key-inherited-table"
                    />
                    <Heading margin="small" level="h2">
                      {I18n.t('Global Keys')}
                    </Heading>
                  </>
                )}
                <InheritedTable
                  prefix="global"
                  label={I18n.t('Global Inherited Developer Keys')}
                  ref={this.setInheritedTableRef}
                  store={store}
                  actions={actions}
                  developerKeysList={globalInheritedList}
                  ctx={ctx}
                  setFocus={this.focusInheritedTab}
                  data-testid="dev-key-inherited-table"
                />
                <View as="div" margin="small" padding="large" textAlign="center">
                  {listInheritedDeveloperKeysPending ? (
                    <Spinner renderTitle={I18n.t('Loading')} />
                  ) : null}
                  {this.showMoreInheritedButton()}
                </View>
              </div>
            </Tabs.Panel>
          )}
        </Tabs>
      </div>
    )
  }
}

DeveloperKeysApp.propTypes = {
  store: PropTypes.shape({
    dispatch: PropTypes.func.isRequired,
  }).isRequired,
  actions: PropTypes.shape({
    developerKeysModalOpen: PropTypes.func.isRequired,
    getRemainingDeveloperKeys: PropTypes.func.isRequired,
    getRemainingInheritedDeveloperKeys: PropTypes.func.isRequired,
    editDeveloperKey: PropTypes.func.isRequired,
  }).isRequired,
  applicationState: PropTypes.shape({
    createOrEditDeveloperKey: PropTypes.shape({
      developerKeyCreateOrEditFailed: PropTypes.bool.isRequired,
      developerKeyCreateOrEditSuccessful: PropTypes.bool.isRequired,
    }),
    listDeveloperKeys: PropTypes.shape({
      nextPage: PropTypes.string,
      listDeveloperKeysPending: PropTypes.bool.isRequired,
      listDeveloperKeysSuccessful: PropTypes.bool.isRequired,
      nextInheritedPage: PropTypes.string,
      listInheritedDeveloperKeysPending: PropTypes.bool.isRequired,
      listInheritedDeveloperKeysSuccessful: PropTypes.bool.isRequired,
      list: PropTypes.arrayOf(DeveloperKey.propTypes.developerKey).isRequired,
      inheritedList: PropTypes.arrayOf(DeveloperKey.propTypes.developerKey).isRequired,
    }).isRequired,
  }).isRequired,
  ctx: PropTypes.shape({
    params: PropTypes.shape({
      contextId: PropTypes.string.isRequired,
    }),
  }).isRequired,
}

export default DeveloperKeysApp
